import Navbar from "../../components/LandPage/Navbar"
import Hero from "../../components/LandPage/Hero"
import Pricing from "../../components/LandPage/Pricing"
import Footer from "../../components/LandPage/Footer"
import TutorialShowcase from "../../components/LandPage/TutorialShowcase"
import IntegrationsCarousel from "../../components/LandPage/IntegrationsCarousel"
import LeadCaptureForm from "../../components/LandPage/LeadCaptureForm"
import { SEO } from "../../seo/Seo"
import { productSchema, softwareSchema, supportFaqSchema, useOrganizationSchema } from "../../seo/schema"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useSignUpContext } from "../../context/useSignUpContext"
import { SignUp } from "../../Objects/SignUp/SignUp"
import { PATH_MAP } from "../../configuration/PathMap"
import { defaultMarketingQuizSlug } from "../../configuration/MarketingQuizzes"
import { trackMarketingEvent } from "../../analytics/tracking"
import { ArrowRight } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { menuScreenshots } from "./menuScreenshots"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"

const carouselIntervalMs = 3600

export default function LandingPage() {
  const { t } = useTranslation("landpage")

  const organizationSchema = useOrganizationSchema()

  const navigate = useNavigate()

  const { updateSignUp } = useSignUpContext()

  const handleLeadQuiz = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "home", target })
    navigate(PATH_MAP.LEAD_QUIZ_LINK(defaultMarketingQuizSlug))
  }

  const handleCreateAccount = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "home", target })
    updateSignUp(new SignUp())
    navigate(PATH_MAP.ROOT_SIGN_UP)
  }

  const [activeScreenshotIndex, setActiveScreenshotIndex] = useState(0)
  
  const [isScreenshotLoading, setIsScreenshotLoading] = useState(true)

  const activeScreenshot = useMemo(() => {
    if (menuScreenshots.length === 0) return null

    return menuScreenshots[activeScreenshotIndex % menuScreenshots.length]
  }, [activeScreenshotIndex])

  useEffect(() => {
    if (menuScreenshots.length <= 1) return

    const intervalId = window.setInterval(() => {
      setIsScreenshotLoading(true)
      setActiveScreenshotIndex((current) => (current + 1) % menuScreenshots.length)
    }, carouselIntervalMs)

    return () => window.clearInterval(intervalId)
  }, [])

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} h-dvh bg-white bg-cover bg-center relative z-0 overflow-x-hidden overflow-y-scroll scrollbar-none`}
    >
      <PublicAnimatedBackground baseColor="#ffffff" />

      <SEO
        title="PDV para restaurante com cupom fiscal, rodízio e cardápio digital"
        description="Mestre da Comanda é o PDV para restaurante e bar completo: pedidos online, cardápio digital, comanda, emissão de cupom fiscal (NFC-e), controle de rodízio e gestão de delivery em um único SaaS."
        path="/"
        structuredData={[organizationSchema, softwareSchema, productSchema, supportFaqSchema]}
      />

      <Navbar showCreateAccountButton={true} />

      <main className="pt-28">
        <Hero />

        <section className="mx-auto items-center justify-center mb-12 mt-2 grid max-w-[1280px] gap-5 px-6 lg:mb-16 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:items-stretch">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
            <aside className="group relative h-min overflow-hidden p-6 transition duration-500 hover:-translate-y-1 lg:flex lg:flex-col lg:justify-between">
              <div className="relative">
                <Animate type={AnimationTypes.slideRightIn} duration="1.15s">
                  <div className="order-1 mx-auto w-full max-w-[320px] sm:max-w-[360px] lg:order-none lg:max-w-[430px]">
                    <div className="relative mx-auto w-full max-w-[280px] rounded-[1.8rem] border border-slate-200 bg-black p-2 shadow-2xl shadow-slate-300/70 sm:max-w-[320px] lg:max-w-[360px]">
                      <div className="relative aspect-[9/18.5] overflow-hidden rounded-[1.4rem] bg-slate-100 lg:rounded-[1.9rem]">
                        {activeScreenshot && (
                          <>
                            <div
                              className={`absolute inset-0 z-10 bg-gradient-to-r from-slate-100 via-white to-slate-100 transition-opacity duration-500 ${
                                isScreenshotLoading ? "opacity-100" : "opacity-0"
                              }`}
                            >
                              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/80 to-transparent animate-[shimmer_1.2s_infinite]" />
                            </div>

                            <Animate key={activeScreenshot.src} type={AnimationTypes.fadeInTraslate} duration="0.9s">
                              <img
                                src={activeScreenshot.src}
                                alt={`${t("free_menu_setup.hero_image_alt")} - ${activeScreenshot.alt}`}
                                className={`h-full w-full rounded-2xl object-cover p-1 transition-all duration-700 ${
                                  isScreenshotLoading ? "scale-[1.02] opacity-0" : "scale-100 opacity-100"
                                }`}
                                loading={activeScreenshotIndex === 0 ? "eager" : "lazy"}
                                onLoad={() => setIsScreenshotLoading(false)}
                                onError={() => setIsScreenshotLoading(false)}
                              />
                            </Animate>

                            {menuScreenshots.length > 1 && (
                              <div className="absolute inset-x-5 bottom-3 z-20 flex gap-1.5 lg:inset-x-6 lg:bottom-4">
                                {menuScreenshots.map((item, index) => (
                                  <button
                                    key={item.src}
                                    type="button"
                                    aria-label={t("free_menu_setup.carousel_dot_aria", { index: index + 1 })}
                                    onClick={() => {
                                      setIsScreenshotLoading(true)
                                      setActiveScreenshotIndex(index)
                                    }}
                                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                                      index === activeScreenshotIndex ? "bg-white" : "bg-white/40"
                                    }`}
                                    data-interaction-description-key="interactionDescriptions:pages.landPage.landPage.button.carouselDotAria"
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Animate>
              </div>

              <div className="relative flex justify-center items-center mt-8">
                <button
                  type="button"
                  onClick={() => handleLeadQuiz("desktop_quiz_teaser")}
                  className="inline-flex min-h-12 w-auto cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-5 text-sm font-black text-white shadow-lg shadow-red-500/20 transition duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-orange-400/30 active:scale-[0.98]"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.landPage.button.handleLeadQuizDesktopQuizTeaser"
                >
                  {t("home_quiz_teaser.cta")}
                  <ArrowRight size={18} className="transition duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </aside>
          </Animate>

          <div className="flex h-full items-center justify-center">
            <LeadCaptureForm
              className="min-w-0"
              i18nPrefix="home_lead_form"
              requireEmailField={false}
              showBusinessNameField={false}
              showEmailField={true}
              showTrafficSourceField={false}
              source="home_landing_lead"
              trafficSourceFallback="Landing principal"
              variant="compact"
            />
          </div>
        </section>

        <IntegrationsCarousel />

        <TutorialShowcase />

        <section id="plans" className="px-2 py-4 mb-5">
          <Pricing showButton={true} />
        </section>
      </main>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="grid grid-cols-1 gap-2 border-t border-orange-200 bg-white/95 p-3 lg:hidden">
          <button
            onClick={() => handleCreateAccount("mobile_sticky_create_account")}
            className="flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-3 py-2 text-center text-sm font-black leading-tight text-white"
            data-interaction-description-key="interactionDescriptions:pages.landPage.landPage.button.handleCreateAccountMobileStickyCreateAccount"
          >
            {t("home_sticky.create_account")}
          </button>

          <button
            onClick={() => handleLeadQuiz("mobile_sticky_lead_quiz")}
            className="flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-3 py-2 text-center text-sm font-black leading-tight text-white shadow-md shadow-sky-500/20"
            data-interaction-description-key="interactionDescriptions:pages.landPage.landPage.button.handleLeadQuizMobileStickyLeadQuiz"
          >
            {t("home_sticky.lead_form")}
          </button>
        </div>
      </Animate>

      <Footer />
    </div>
  )
}
